import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles, RefreshCw, ArrowUpRight, Info,
    CircleDot, Compass, Lightbulb, Quote,
} from "lucide-react";
import { OperationalShell } from "../components/OperationalShell";
import { useAuth } from "../../lib/auth";
import { useCapability } from "../useCapability";
import { guidedActionToHref } from "../guidedAction";
import { GuidanceTooltip } from "../guidance/GuidanceTooltip";
import { useDemo } from "../demo/DemoContext";
import api from "../../lib/api";

/**
 * Operational Intelligence Briefing — Phase 7 (v4.2.0).
 *
 * Narrative-first, calm, executive-readable. Reads sentences verbatim
 * from `/api/operational-base/analytics/briefing` and lays them out
 * with generous whitespace. Never a "data graveyard."
 *
 * Layout (top → bottom):
 *   1. Headline card    — single calm sentence (the emotional anchor)
 *   2. Outliers         — 0–3 supportive cards, "worth a closer look"
 *   3. Regional view    — restrained side-by-side cards
 *   4. Pulse narrative  — 1–3 sentences of network activity
 *   5. Guidance         — calm suggestions, never coercive
 *   6. Explainability   — where this came from (collapsible)
 */
const Analytics = () => {
    const { t, i18n } = useTranslation();
    const nav = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const { map } = useCapability();
    const demo = useDemo();
    const [briefing, setBriefing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const lang = (i18n.language || "en").split("-")[0];
            const url = demo.isActive && demo.persona
                ? `/operational-base/demo/personas/${demo.persona.key}/briefing?lang=${lang}`
                : "/operational-base/analytics/briefing";
            const res = await api.get(url);
            setBriefing(res.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.analytics.error"));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ },
        [demo.isActive, demo.persona?.key, i18n.language]);

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!map) return null;

    return (
        <OperationalShell breadcrumb={[{ label: t("ob.analytics.eyebrow") }]}>
            <header className="mb-10 lg:mb-14 flex items-start justify-between gap-6 flex-wrap" data-testid="ob-analytics-header">
                <div className="space-y-3 min-w-0">
                    <p className="ob-eyebrow">{t("ob.analytics.eyebrow")}</p>
                    <h1
                        className="text-4xl sm:text-5xl flex items-center"
                        style={{ color: "var(--ob-text-primary)" }}
                        data-testid="ob-analytics-title"
                    >
                        {t("ob.analytics.title")}
                        <GuidanceTooltip guidanceKey="analytics.briefing.intro" />
                    </h1>
                    <p
                        className="text-base max-w-2xl"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        {t("ob.analytics.subtitle")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => load(true)}
                    disabled={refreshing}
                    data-testid="ob-analytics-refresh-btn"
                    className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 disabled:opacity-50"
                    style={{
                        color: "var(--ob-text-secondary)",
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <RefreshCw size={12} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
                    {t("ob.analytics.refresh")}
                </button>
            </header>

            {loading && (
                <p
                    data-testid="ob-analytics-loading"
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                >
                    {t("ob.analytics.loading")}
                </p>
            )}

            {!loading && briefing && briefing.quiet_network && (
                <QuietNetworkCard />
            )}

            {!loading && briefing && !briefing.quiet_network && (
                <div className="space-y-12 lg:space-y-16">
                    {/* 1. Headline */}
                    <HeadlineCard
                        scopeLabel={briefing.scope_label}
                        headline={briefing.headline}
                        totals={briefing.totals}
                    />

                    {/* 2. Outliers */}
                    {briefing.outliers && briefing.outliers.length > 0 && (
                        <Section
                            icon={<Compass size={14} strokeWidth={1.5} />}
                            label={t("ob.analytics.outliers_section")}
                            testId="ob-analytics-outliers"
                            guidanceKey="analytics.outliers.intro"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {briefing.outliers.map((o) => (
                                    <OutlierCard
                                        key={o.id}
                                        outlier={o}
                                        onOpen={() => {
                                            const href = guidedActionToHref(o.guided_action);
                                            if (href) nav(href);
                                            else if (o.org_id) nav("/operational-base/modules");
                                        }}
                                    />
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* 3. Regional view */}
                    {briefing.regions && briefing.regions.length > 0 && (
                        <Section
                            icon={<CircleDot size={14} strokeWidth={1.5} />}
                            label={t("ob.analytics.regions_section")}
                            testId="ob-analytics-regions"
                            guidanceKey="analytics.regions.intro"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {briefing.regions.map((r) => (
                                    <RegionCard key={r.org_id} region={r} />
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* 4. Narrative */}
                    {briefing.narrative && briefing.narrative.length > 0 && (
                        <Section
                            icon={<Quote size={14} strokeWidth={1.5} />}
                            label={t("ob.analytics.narrative_section")}
                            testId="ob-analytics-narrative"
                            guidanceKey="analytics.narrative.intro"
                        >
                            <div className="ob-card p-6 sm:p-8 space-y-3">
                                {briefing.narrative.map((line, idx) => (
                                    <p
                                        key={`narrative-${idx}-${(line.sentence || '').slice(0, 24)}`}
                                        className="text-base leading-relaxed"
                                        style={{ color: "var(--ob-text-primary)" }}
                                        data-testid={`ob-analytics-narrative-line-${idx}`}
                                    >
                                        {line.sentence}
                                    </p>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* 5. Guidance */}
                    {briefing.guidance && briefing.guidance.length > 0 && (
                        <Section
                            icon={<Lightbulb size={14} strokeWidth={1.5} />}
                            label={t("ob.analytics.guidance_section")}
                            testId="ob-analytics-guidance"
                            guidanceKey="analytics.guidance.intro"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {briefing.guidance.map((g) => (
                                    <GuidanceCard
                                        key={g.id}
                                        guidance={g}
                                        onOpen={() => {
                                            const href = guidedActionToHref(g.guided_action);
                                            if (href) nav(href);
                                            else if (g.suggested_path) nav(g.suggested_path);
                                        }}
                                    />
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* 6. Explainability */}
                    <Explainability explainability={briefing.explainability} />
                </div>
            )}
        </OperationalShell>
    );
};


// ─────────────────────────────────────────────────────────────────────
// Building blocks
// ─────────────────────────────────────────────────────────────────────

const TONE_RING = {
    calm: "rgba(16,185,129,0.30)",
    supportive: "rgba(59,130,246,0.30)",
    attention: "rgba(244,114,182,0.35)",
};
const TONE_TINT = {
    calm: "rgba(16,185,129,0.06)",
    supportive: "rgba(59,130,246,0.05)",
    attention: "rgba(244,114,182,0.06)",
};
const TONE_DOT = {
    calm: "#10B981",
    supportive: "#3B82F6",
    attention: "#F472B6",
};

const Section = ({ icon, label, children, testId, guidanceKey }) => (
    <section data-testid={testId}>
        <div className="flex items-center gap-2 mb-5">
            <span
                aria-hidden="true"
                style={{ color: "var(--ob-text-tertiary)" }}
                className="inline-flex"
            >
                {icon}
            </span>
            <p className="ob-eyebrow !mb-0">{label}</p>
            {guidanceKey && <GuidanceTooltip guidanceKey={guidanceKey} />}
        </div>
        {children}
    </section>
);

const HeadlineCard = ({ scopeLabel, headline, totals }) => {
    const tone = headline?.tone || "calm";
    return (
        <section
            data-testid="ob-analytics-headline"
            className="rounded-2xl p-8 sm:p-10 lg:p-12"
            style={{
                background: TONE_TINT[tone],
                border: `1px solid ${TONE_RING[tone]}`,
                boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset",
            }}
        >
            <div className="flex items-center gap-2 mb-3">
                <span
                    aria-hidden="true"
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: TONE_DOT[tone] }}
                />
                <p
                    className="text-xs uppercase tracking-[0.18em]"
                    style={{ color: "var(--ob-text-tertiary)", fontFamily: "Outfit, sans-serif" }}
                >
                    {scopeLabel}
                </p>
            </div>
            <p
                className="text-2xl sm:text-3xl leading-snug"
                style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                data-testid="ob-analytics-headline-sentence"
            >
                {headline.sentence}
            </p>
            <div
                className="mt-6 pt-5 flex items-center gap-6 flex-wrap text-xs"
                style={{
                    color: "var(--ob-text-tertiary)",
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                }}
            >
                <Pill label="Locations" value={totals?.location_count ?? 0} />
                <Pill label="Aligned" value={totals?.aligned_locations ?? 0} />
                <Pill label="Independent" value={totals?.independent_locations ?? 0} />
                <Pill label="Active overrides" value={totals?.locations_with_active_override ?? 0} />
            </div>
        </section>
    );
};

const Pill = ({ label, value }) => (
    <span className="inline-flex items-center gap-2">
        <span style={{ color: "var(--ob-text-tertiary)" }}>{label}</span>
        <span
            className="px-2 py-0.5 rounded-full"
            style={{
                color: "var(--ob-text-secondary)",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
            }}
        >
            {value}
        </span>
    </span>
);

const RegionCard = ({ region }) => {
    const { t } = useTranslation();
    const tone = region.tone || "calm";
    return (
        <article
            data-testid={`ob-analytics-region-${region.org_id}`}
            className="ob-card p-5 sm:p-6"
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <p
                    className="text-base font-medium min-w-0 truncate"
                    style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                >
                    {region.name}
                </p>
                <span
                    className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{
                        color: TONE_DOT[tone],
                        background: TONE_TINT[tone],
                        border: `1px solid ${TONE_RING[tone]}`,
                    }}
                    data-testid={`ob-analytics-region-${region.org_id}-alignment`}
                >
                    {t("ob.analytics.alignment_value", { n: region.alignment_percent })}
                </span>
            </div>
            <p
                className="text-sm leading-relaxed mb-4"
                style={{ color: "var(--ob-text-secondary)" }}
            >
                {region.summary_sentence}
            </p>
            <div
                className="flex items-center gap-4 text-xs"
                style={{ color: "var(--ob-text-tertiary)" }}
            >
                <span>
                    {region.location_count} {t("ob.analytics.region.locations_label").toLowerCase()}
                </span>
                {region.active_overrides > 0 && (
                    <span>
                        {region.active_overrides} {t("ob.analytics.region.overrides_label").toLowerCase()}
                    </span>
                )}
            </div>
        </article>
    );
};

const OutlierCard = ({ outlier, onOpen }) => {
    const { t } = useTranslation();
    const tone = outlier.tone || "supportive";
    return (
        <article
            data-testid={`ob-analytics-outlier-${outlier.id}`}
            className="rounded-2xl p-5 sm:p-6"
            style={{
                background: TONE_TINT[tone],
                border: `1px solid ${TONE_RING[tone]}`,
            }}
        >
            <p
                className="text-base font-medium mb-2"
                style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
            >
                {outlier.title}
            </p>
            {outlier.subtitle && (
                <p
                    className="text-sm mb-4 leading-relaxed"
                    style={{ color: "var(--ob-text-secondary)" }}
                >
                    {outlier.subtitle}
                </p>
            )}
            {outlier.evidence && outlier.evidence.length > 0 && (
                <div className="mt-3 pt-3" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
                    <p
                        className="text-xs uppercase tracking-wider mb-2"
                        style={{ color: "var(--ob-text-tertiary)" }}
                    >
                        {t("ob.analytics.outlier.evidence_label")}
                    </p>
                    <ul className="space-y-1">
                        {outlier.evidence.map((line, idx) => (
                            <li
                                key={`outlier-evid-${outlier.id}-${idx}-${String(line).slice(0, 16)}`}
                                className="text-xs leading-relaxed"
                                style={{ color: "var(--ob-text-secondary)" }}
                            >
                                · {line}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {(outlier.org_id || outlier.guided_action) && onOpen && (
                <button
                    type="button"
                    onClick={onOpen}
                    data-testid={`ob-analytics-outlier-${outlier.id}-open`}
                    className="mt-4 inline-flex items-center gap-1 text-xs rounded-full px-3 py-1"
                    style={{
                        color: "#A7F3D0",
                        background: "rgba(16,185,129,0.10)",
                        border: "1px solid rgba(16,185,129,0.25)",
                    }}
                >
                    <ArrowUpRight size={11} strokeWidth={1.5} />
                    {outlier.guided_action?.label || t("ob.analytics.outlier.open_org")}
                </button>
            )}
        </article>
    );
};

const GuidanceCard = ({ guidance, onOpen }) => {
    const { t } = useTranslation();
    const tone = guidance.tone || "supportive";
    return (
        <article
            data-testid={`ob-analytics-guidance-${guidance.id}`}
            className="ob-card p-5 sm:p-6"
            style={{ borderLeft: `2px solid ${TONE_DOT[tone]}` }}
        >
            <p
                className="text-base font-medium mb-2"
                style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
            >
                {guidance.title}
            </p>
            {guidance.subtitle && (
                <p
                    className="text-sm leading-relaxed mb-4"
                    style={{ color: "var(--ob-text-secondary)" }}
                >
                    {guidance.subtitle}
                </p>
            )}
            {guidance.evidence && guidance.evidence.length > 0 && (
                <details className="mb-3 group">
                    <summary
                        className="text-xs cursor-pointer select-none"
                        style={{ color: "var(--ob-text-tertiary)" }}
                    >
                        {t("ob.analytics.outlier.evidence_label")}
                    </summary>
                    <ul className="mt-2 space-y-1">
                        {guidance.evidence.map((line, idx) => (
                            <li
                                key={`guidance-evid-${guidance.id}-${idx}-${String(line).slice(0, 16)}`}
                                className="text-xs leading-relaxed"
                                style={{ color: "var(--ob-text-secondary)" }}
                            >
                                · {line}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
            {(guidance.suggested_path || guidance.guided_action) && onOpen && (
                <button
                    type="button"
                    onClick={onOpen}
                    data-testid={`ob-analytics-guidance-${guidance.id}-open`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1"
                    style={{
                        color: "#A7F3D0",
                        background: "rgba(16,185,129,0.10)",
                        border: "1px solid rgba(16,185,129,0.25)",
                    }}
                >
                    <ArrowUpRight size={11} strokeWidth={1.5} />
                    {guidance.guided_action?.label || t("ob.analytics.guidance.open_guided")}
                </button>
            )}
        </article>
    );
};

const QuietNetworkCard = () => {
    const { t } = useTranslation();
    return (
        <section
            data-testid="ob-analytics-quiet"
            className="ob-card p-10 text-center"
        >
            <Sparkles
                size={28}
                strokeWidth={1.5}
                aria-hidden="true"
                className="mx-auto mb-4"
                style={{ color: "var(--ob-text-tertiary)" }}
            />
            <p
                className="text-2xl mb-3"
                style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
            >
                {t("ob.analytics.quiet.title")}
            </p>
            <p
                className="text-base"
                style={{ color: "var(--ob-text-secondary)" }}
            >
                {t("ob.analytics.quiet.subtitle")}
            </p>
        </section>
    );
};

const Explainability = ({ explainability }) => {
    const { t } = useTranslation();
    if (!explainability) return null;
    return (
        <section data-testid="ob-analytics-explainability">
            <details className="ob-card p-5">
                <summary
                    className="cursor-pointer select-none flex items-center gap-2 text-xs"
                    style={{ color: "var(--ob-text-tertiary)" }}
                >
                    <Info size={12} strokeWidth={1.5} aria-hidden="true" />
                    {t("ob.analytics.explainability_label")}
                </summary>
                <div
                    className="mt-3 pt-3 space-y-2 text-xs"
                    style={{
                        color: "var(--ob-text-secondary)",
                        borderTop: "1px dashed rgba(255,255,255,0.08)",
                    }}
                >
                    <p>{t("ob.analytics.explainability_intro")}</p>
                    <p>
                        {t("ob.analytics.explainability_sources", {
                            sources: (explainability.data_sources || []).join(" · "),
                        })}
                    </p>
                    <p>{t("ob.analytics.explainability_window", { days: explainability.window_days })}</p>
                    <p>{t("ob.analytics.explainability_scope", { count: explainability.scope_org_count })}</p>
                    {explainability.note && (
                        <p style={{ color: "var(--ob-text-tertiary)" }}>{explainability.note}</p>
                    )}
                </div>
            </details>
        </section>
    );
};

export default Analytics;
